import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    ListTodo, ArrowLeft, CalendarClock, Flame, Circle, CircleDot, CheckCircle2,
    AlertTriangle, ExternalLink, Folder, ChevronDown, Check,
} from 'lucide-react';
import { hubAPI } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const STATUS_META = {
    todo: { icon: Circle, label: 'À faire', next: 'in_progress' },
    in_progress: { icon: CircleDot, label: 'En cours', next: 'done' },
    done: { icon: CheckCircle2, label: 'Terminée', next: 'todo' },
};

const importanceColor = (n) => {
    if (n >= 8) return 'bg-red-100 text-red-700';
    if (n >= 5) return 'bg-amber-100 text-amber-700';
    if (n >= 3) return 'bg-blue-100 text-blue-700';
    return 'bg-slate-100 text-slate-600';
};

const daysLeft = (iso) => {
    if (!iso) return null;
    return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
};

export default function HubMyTasksPage() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const initialFilter = params.get('filter') || 'overdue';
    const [filter, setFilter] = useState(initialFilter);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await hubAPI.myTasks(filter === 'all' ? undefined : filter);
            setTasks(r.data?.tasks || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    // Recharge dès que l'utilisateur revient sur l'onglet ou navigue vers cette page.
    // Sinon une tâche marquée "done" ailleurs (édition depuis la page projet) resterait
    // affichée en retard tant qu'on n'actualise pas manuellement.
    useEffect(() => {
        const onFocus = () => load();
        const onVisibility = () => { if (!document.hidden) load(); };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [load]);

    useEffect(() => {
        const nextParams = new URLSearchParams(params);
        if (filter === 'all') nextParams.delete('filter');
        else nextParams.set('filter', filter);
        setParams(nextParams, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const counts = useMemo(() => ({
        overdue: tasks.filter((t) => t.is_overdue).length,
        open: tasks.filter((t) => t.status === 'todo' || t.status === 'in_progress').length,
        done: tasks.filter((t) => t.status === 'done').length,
        all: tasks.length,
    }), [tasks]);

    const toggleStatus = async (task, e) => {
        e.stopPropagation();
        const next = STATUS_META[task.status]?.next || 'done';
        try {
            const r = await hubAPI.updateTask(task.id, { status: next });
            const newStatus = r.data?.status || next;
            // Recompute is_overdue based on new status : une tâche 'done' n'est JAMAIS en retard.
            // Règle métier : en retard = deadline < début du jour local (pas le jour même).
            const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
            const cutoffIso = startOfToday.toISOString();
            const nextOverdue = !!(task.deadline && task.deadline < cutoffIso && newStatus !== 'done');
            setTasks((prev) => prev.map((t) => (
                t.id === task.id
                    ? {
                        ...t, ...r.data,
                        // Conserver les champs enrichis backend (project_*, stage_*)
                        project_name: t.project_name,
                        project_id: t.project_id,
                        project_space_id: t.project_space_id,
                        project_status: t.project_status,
                        project_current_stage_id: t.project_current_stage_id,
                        stage_name: t.stage_name,
                        stage_color: t.stage_color,
                        is_overdue: nextOverdue,
                    }
                    : t
            )));
            toast.success('Statut mis à jour');
            // Si un filtre est actif et que la tâche n'y correspond plus, on recharge pour clean.
            if (filter !== 'all') load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const openProject = (task, e) => {
        e.stopPropagation();
        navigate(`/projets/projet/${task.project_id}`);
    };

    // Déplace la tâche vers une autre étape en un clic — depuis la page « Mes tâches Hub ».
    // Le backend applique la cohérence stage ↔ status : si l'étape cible est finale
    // (nom Terminé/Fini/Gagné/Livré/... OU dernière du pipeline), le statut passe à done.
    const changeStage = async (task, newStageId, e) => {
        e?.stopPropagation();
        if (!newStageId || newStageId === task.stage_id) return;
        try {
            const r = await hubAPI.updateTask(task.id, { stage_id: newStageId });
            const newStatus = r.data?.status ?? task.status;
            const stageOption = (task.project_pipeline || []).find((s) => s.id === newStageId);
            // Règle métier : en retard = deadline < début du jour local (pas le jour même).
            const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
            const cutoffIso = startOfToday.toISOString();
            const nextOverdue = !!(task.deadline && task.deadline < cutoffIso && newStatus !== 'done');
            setTasks((prev) => prev.map((t) => (
                t.id === task.id
                    ? {
                        ...t, ...r.data,
                        project_name: t.project_name,
                        project_id: t.project_id,
                        project_space_id: t.project_space_id,
                        project_status: t.project_status,
                        project_current_stage_id: t.project_current_stage_id,
                        project_pipeline: t.project_pipeline,
                        stage_name: stageOption?.name || t.stage_name,
                        stage_color: stageOption?.color || t.stage_color,
                        is_overdue: nextOverdue,
                    }
                    : t
            )));
            toast.success(`Tâche déplacée sur « ${stageOption?.name || 'nouvelle étape'} »`);
            // Si le statut a changé côté serveur ou si le filtre actif ne matche plus, on recharge.
            if (newStatus !== task.status || filter !== 'all') load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Déplacement impossible');
        }
    };

    return (
        <DashboardLayout>
            <div className="max-w-6xl mx-auto space-y-4 p-4 sm:p-6">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/projets')} data-testid="btn-back-hub">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Retour au Hub
                    </Button>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <ListTodo className="w-5 h-5 text-indigo-600" />
                            Mes tâches Hub
                            {counts.overdue > 0 && (
                                <Badge variant="destructive" className="ml-2" data-testid="my-hub-tasks-overdue-badge">
                                    <AlertTriangle className="w-3 h-3 mr-1" /> {counts.overdue} en retard
                                </Badge>
                            )}
                        </CardTitle>
                        <p className="text-xs text-slate-500 pt-1">
                            Toutes les tâches qui te sont assignées sur les projets du Hub. Clique sur une ligne pour
                            ouvrir le projet et l&apos;étape correspondants.
                        </p>
                    </CardHeader>

                    <CardContent className="space-y-3">
                        <Tabs value={filter} onValueChange={setFilter}>
                            <TabsList className="grid grid-cols-4 w-full sm:w-auto">
                                <TabsTrigger value="overdue" data-testid="tab-overdue">
                                    En retard ({counts.overdue})
                                </TabsTrigger>
                                <TabsTrigger value="open" data-testid="tab-open">
                                    Ouvertes ({counts.open})
                                </TabsTrigger>
                                <TabsTrigger value="done" data-testid="tab-done">
                                    Terminées ({counts.done})
                                </TabsTrigger>
                                <TabsTrigger value="all" data-testid="tab-all">
                                    Toutes ({counts.all})
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>

                        {loading ? (
                            <div className="text-center py-8 text-slate-400 text-sm">Chargement…</div>
                        ) : tasks.length === 0 ? (
                            <div className="text-center py-10 text-slate-500">
                                <ListTodo className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                                <p className="text-sm">Aucune tâche dans ce filtre.</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {tasks.map((t) => {
                                    const StatusIcon = STATUS_META[t.status]?.icon || Circle;
                                    const days = daysLeft(t.deadline);
                                    return (
                                        <div
                                            key={t.id}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => navigate(`/projets/projet/${t.project_id}`)}
                                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate(`/projets/projet/${t.project_id}`)}
                                            className={cn(
                                                'group flex items-start gap-3 rounded-lg border bg-white p-3 hover:shadow-sm hover:border-indigo-300 transition-all cursor-pointer',
                                                t.is_overdue && 'border-red-300 bg-red-50/40',
                                                t.status === 'done' && 'opacity-70'
                                            )}
                                            data-testid={`my-hub-task-row-${t.id}`}
                                        >
                                            <button
                                                type="button"
                                                onClick={(e) => toggleStatus(t, e)}
                                                className="shrink-0 mt-0.5"
                                                title="Changer le statut"
                                                data-testid={`my-hub-task-toggle-${t.id}`}
                                            >
                                                <StatusIcon className={cn(
                                                    'w-5 h-5',
                                                    t.status === 'done' ? 'text-emerald-500' :
                                                        t.status === 'in_progress' ? 'text-blue-500' : 'text-slate-400'
                                                )} />
                                            </button>

                                            <div className="flex-1 min-w-0">
                                                <div className={cn('text-sm font-medium text-slate-900', t.status === 'done' && 'line-through text-slate-400')}>
                                                    {t.name}
                                                </div>
                                                <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[11px]">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => openProject(t, e)}
                                                        className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 hover:underline"
                                                        title="Ouvrir le projet"
                                                        data-testid={`my-hub-task-project-${t.id}`}
                                                    >
                                                        <Folder className="w-3 h-3" />
                                                        {t.project_name || 'Projet'}
                                                    </button>
                                                    {t.stage_name && (
                                                        (t.project_pipeline || []).length > 0 ? (
                                                            <Popover>
                                                                <PopoverTrigger asChild>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="inline-flex items-center gap-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-0.5 transition-colors cursor-pointer"
                                                                        title="Cliquer pour déplacer la tâche sur une autre étape"
                                                                        data-testid={`my-hub-task-stage-trigger-${t.id}`}
                                                                    >
                                                                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.stage_color || '#94a3b8' }} />
                                                                        {t.stage_name}
                                                                        <ChevronDown className="w-2.5 h-2.5" />
                                                                    </button>
                                                                </PopoverTrigger>
                                                                <PopoverContent
                                                                    align="start"
                                                                    className="w-56 p-1"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    data-testid={`my-hub-task-stage-popover-${t.id}`}
                                                                >
                                                                    <div className="px-2 py-1 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                                                                        Déplacer sur…
                                                                    </div>
                                                                    <div className="max-h-64 overflow-y-auto">
                                                                        {t.project_pipeline.map((s) => (
                                                                            <button
                                                                                key={s.id}
                                                                                type="button"
                                                                                onClick={(e) => changeStage(t, s.id, e)}
                                                                                className={cn(
                                                                                    'w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-slate-100 text-left transition-colors',
                                                                                    s.id === t.stage_id && 'bg-indigo-50 text-indigo-700 font-semibold'
                                                                                )}
                                                                                data-testid={`my-hub-task-stage-option-${t.id}-${s.id}`}
                                                                            >
                                                                                <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                                                                                <span className="flex-1 truncate">{s.name}</span>
                                                                                {s.id === t.stage_id && <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                </PopoverContent>
                                                            </Popover>
                                                        ) : (
                                                            <span
                                                                className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 px-2 py-0.5"
                                                                title={`Étape : ${t.stage_name}`}
                                                            >
                                                                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.stage_color || '#94a3b8' }} />
                                                                {t.stage_name}
                                                            </span>
                                                        )
                                                    )}
                                                    {typeof t.importance === 'number' && (
                                                        <span className={cn('inline-flex items-center gap-0.5 rounded-full px-2 py-0.5', importanceColor(t.importance))}>
                                                            <Flame className="w-3 h-3" /> {t.importance}
                                                        </span>
                                                    )}
                                                    {t.deadline && (
                                                        <span className={cn(
                                                            'inline-flex items-center gap-1 rounded-full px-2 py-0.5',
                                                            t.is_overdue ? 'bg-red-100 text-red-700 font-semibold' :
                                                                days != null && days <= 2 ? 'bg-amber-100 text-amber-700' :
                                                                    'bg-slate-100 text-slate-600'
                                                        )}>
                                                            <CalendarClock className="w-3 h-3" />
                                                            {t.is_overdue ? `Retard ${Math.abs(days)}j` :
                                                                days === 0 ? "Aujourd'hui" :
                                                                    `J-${days}`}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                                                onClick={(e) => openProject(t, e)}
                                                title="Ouvrir le projet"
                                                data-testid={`my-hub-task-open-${t.id}`}
                                            >
                                                <ExternalLink className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </DashboardLayout>
    );
}
